/**
 * More than one company on one phone.
 *
 * The agent has always synced a list of companies (`config.json`); the app only
 * ever held one connection, so a trader with two firms needed two devices or a
 * lot of retyping. This turns the single connection into a list with one of
 * them active.
 *
 * MIGRATION MATTERS MORE THAN THE FEATURE. Every existing install has its
 * connection in the three old keys. Reading the list and finding it empty must
 * not present the setup screen to someone who set this up months ago — so the
 * old keys are folded in on first load, and kept written afterwards.
 *
 * Each company's snapshot already lives under its own IndexedDB key
 * (`snap:{companyId}`), so isolation is a property of the storage layer and not
 * something this module has to enforce.
 */
import { SETTINGS, getSetting, setSetting } from "./sync/db.js";

export type Connection = { baseUrl: string; readToken: string; companyId: string };

const trim = (c: Connection): Connection => ({
  baseUrl: c.baseUrl.trim(),
  readToken: c.readToken.trim(),
  companyId: c.companyId.trim(),
});

export type Connections = {
  list: Connection[];
  /** null only when nothing is configured at all */
  active: Connection | null;
};

export async function loadConnections(): Promise<Connections> {
  const stored = await getSetting<Connection[]>(SETTINGS.connections, []);
  const list = Array.isArray(stored) ? stored.filter(isConnection).map(trim) : [];

  // Fold in a pre-multi-company install.
  if (list.length === 0) {
    const legacy = trim({
      baseUrl: await getSetting(SETTINGS.baseUrl, ""),
      readToken: await getSetting(SETTINGS.readToken, ""),
      companyId: await getSetting(SETTINGS.companyId, ""),
    });
    if (legacy.baseUrl && legacy.readToken && legacy.companyId) {
      list.push(legacy);
      await setSetting(SETTINGS.connections, list);
    }
  }

  if (list.length === 0) return { list, active: null };

  const activeId = await getSetting(SETTINGS.activeCompany, "");
  // A stored active id can point at a company that has since been removed —
  // falling back to the first entry beats rendering an empty app.
  const active = list.find((c) => c.companyId === activeId) ?? list[0]!;
  return { list, active };
}

function isConnection(value: unknown): value is Connection {
  if (typeof value !== "object" || value === null) return false;
  const c = value as Record<string, unknown>;
  return (
    typeof c["baseUrl"] === "string" &&
    typeof c["readToken"] === "string" &&
    typeof c["companyId"] === "string"
  );
}

/**
 * Add or update a company, and make it the active one.
 *
 * Matching on `companyId` means re-saving an existing company edits it rather
 * than creating a duplicate that shadows the original in the switcher.
 */
export async function saveConnectionEntry(entry: Connection): Promise<Connections> {
  const clean = trim(entry);
  const { list } = await loadConnections();

  const next = list.filter((c) => c.companyId !== clean.companyId);
  next.push(clean);

  await setSetting(SETTINGS.connections, next);
  await setSetting(SETTINGS.activeCompany, clean.companyId);
  await writeLegacy(clean);

  return { list: next, active: clean };
}

export async function switchTo(companyId: string): Promise<Connections> {
  const { list } = await loadConnections();
  const target = list.find((c) => c.companyId === companyId);
  if (!target) return loadConnections();

  await setSetting(SETTINGS.activeCompany, companyId);
  await writeLegacy(target);
  return { list, active: target };
}

/**
 * Forget a company.
 *
 * Deliberately does NOT delete its snapshot: "remove this company from the
 * switcher" and "destroy the local copy of its books" are different intentions,
 * and Settings has a separate, explicit button for the second.
 */
export async function removeConnection(companyId: string): Promise<Connections> {
  const { list } = await loadConnections();
  const next = list.filter((c) => c.companyId !== companyId);
  await setSetting(SETTINGS.connections, next);

  const active = next[0] ?? null;
  await setSetting(SETTINGS.activeCompany, active?.companyId ?? "");
  if (active) await writeLegacy(active);

  return { list: next, active };
}

/** Keep the old single-connection keys pointing at whatever is active. */
async function writeLegacy(connection: Connection): Promise<void> {
  await setSetting(SETTINGS.baseUrl, connection.baseUrl);
  await setSetting(SETTINGS.readToken, connection.readToken);
  await setSetting(SETTINGS.companyId, connection.companyId);
}
